/*
 * Founding Partner Program onboarding — Wave 2.
 * Minimal, payment-free entry for pilot participants. No pricing / quote / PO.
 *   /myschoolrx/pilot          -> PilotEntry (choose path)
 *   /myschoolrx/pilot/school    -> PilotSingleSchool
 *   /myschoolrx/pilot/portfolio -> PilotPortfolio
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { markBrowserSession } from "@/lib/sessionPersistence";
import { pilotApi } from "../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket, School, Building2, ArrowRight, ArrowLeft, CheckCircle2, Loader2, ShieldCheck, Landmark, Church, Network, GraduationCap, Users } from "lucide-react";
import { useGuestName } from "./GuestName";

const API = process.env.REACT_APP_BACKEND_URL;
const ROUTE = "/myschoolrx";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#f9fafb]" data-testid="pilot-onboarding">
    <header className="bg-white border-b border-[#e5e7eb]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href={ROUTE} className="flex items-center gap-2" data-testid="pilot-logo">
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-8 w-auto" />
        </a>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#2563eb] font-semibold">
          <Rocket className="h-3.5 w-3.5" /> Founding Partner Program
        </div>
      </div>
    </header>
    <main id="main" className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 py-8 sm:py-12">{children}</main>
  </div>
);

const Field = ({ label, required, children }) => {
  const id = `f-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  const child = React.isValidElement(children)
    ? React.cloneElement(children, { id: children.props.id || id })
    : children;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-[13px] font-medium text-[#374151]">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {child}
    </div>
  );
};

/* Founding Partner consent: one source of truth for /pilot and both setup pages (survives refresh/Back). */
const CONSENT_KEY = "fp_pilot_consent";
const readConsent = () => {
  try { const v = JSON.parse(sessionStorage.getItem(CONSENT_KEY) || "null"); return v?.agreed ? v : null; } catch { return null; }
};
const usePilotConsent = () => {
  const [consent, setConsent] = useState(readConsent);
  const update = (agreed) => {
    const v = agreed ? { agreed: true, at: readConsent()?.at || new Date().toISOString() } : null;
    try { v ? sessionStorage.setItem(CONSENT_KEY, JSON.stringify(v)) : sessionStorage.removeItem(CONSENT_KEY); } catch { /* state still drives the page */ }
    setConsent(v);
  };
  return [consent, update];
};
const CONSENT_TEXT = "I understand and agree to participate as a Founding Partner and provide feedback.";

const InlineConsent = ({ checked, onChange, testid }) => (
  <label className="flex items-start gap-2.5 rounded-lg border border-[#fde68a] bg-[#fffbeb] p-3 cursor-pointer select-none" data-testid={`${testid}-inline-consent`}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563eb]" data-testid={`${testid}-consent-checkbox`} />
    <span className="text-[13.5px] font-medium text-[#111827]">{CONSENT_TEXT}</span>
  </label>
);

const NoPaymentNote = () => (
  <div className="flex items-start gap-2 rounded-lg border border-[#dbeafe] bg-[#eff6ff] px-3 py-2.5 text-[13px] text-[#1e40af]" data-testid="pilot-no-payment-note">
    <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
    <span>You are participating in MySchoolRx Founding Partner Program. No payment is required.</span>
  </div>
);

/* ── Entry: "What type of organization are you?" → routes to the right wizard ── */
const ORG_TYPE_OPTIONS = [
  { key: "single_school", label: "Single School", desc: "One school setting up its own Diagnostic.", icon: School, multi: false },
  { key: "school_district", label: "School District", desc: "A district overseeing multiple schools.", icon: Landmark, multi: true },
  { key: "charter_authorizer", label: "Charter Authorizer", desc: "An authorizer monitoring a portfolio of schools.", icon: ShieldCheck, multi: true },
  { key: "cmo", label: "Charter Management Org (CMO)", desc: "A CMO operating several charter schools.", icon: Building2, multi: true },
  { key: "emo", label: "Education Management Org (EMO)", desc: "An EMO managing a network of schools.", icon: Network, multi: true },
  { key: "private_school_network", label: "Private School Network", desc: "A network of private schools.", icon: GraduationCap, multi: true },
  { key: "diocese", label: "Diocese / Faith-Based Network", desc: "A diocese or faith-based school network.", icon: Church, multi: true },
  { key: "state_education_agency", label: "State Education Agency", desc: "A state agency working across schools.", icon: Landmark, multi: true },
  { key: "other", label: "Other", desc: "Another kind of multi-school organization.", icon: Users, multi: true },
];

export const PilotEntry = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [consent, setConsent] = usePilotConsent();
  const agreed = !!consent;
  const setAgreed = setConsent;
  const { name: guestName } = useGuestName();
  const ctx = new URLSearchParams(window.location.search).get("context");
  useEffect(() => { pilotApi.getPublic().then(setCfg).catch(() => setCfg({})); }, []);

  const singleEnabled = cfg?.single_school_enabled !== false;
  const multiEnabled = cfg?.portfolio_enabled !== false;

  const choose = (opt) => {
    if (!agreed) { toast.error("Please acknowledge the Founding Partner agreement to continue."); return; }
    if (opt.multi) navigate(`${ROUTE}/pilot/portfolio?orgtype=${opt.key}`);
    else navigate(`${ROUTE}/pilot/school`);
  };

  const options = ORG_TYPE_OPTIONS.filter((o) => (o.multi ? multiEnabled : singleEnabled));
  const isMatch = (o) => (ctx === "school" && !o.multi) || (ctx === "portfolio" && o.multi);

  return (
    <Shell>
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#2563eb] font-semibold">Get started as a Founding Partner</span>
        <p className="mt-2 text-[15px] font-semibold text-[#00698a]" data-testid="pilot-fp-greeting">
          {guestName ? `${guestName}, you're one step from your Founding Partner access.` : "You're one step from your Founding Partner access."}
        </p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0b1220] leading-tight">What type of organization are you?</h1>
        <p className="mt-3 text-[15px] text-[#4b5563]">No payment, quote, or purchase order required. Pick the option that best describes you — we'll set up the right workspace automatically.</p>
      </div>

      {/* Founding Partner acknowledgement — required before continuing */}
      <div className="mt-6 max-w-2xl rounded-xl border border-[#dbeafe] bg-[#eff6ff] p-4 sm:p-5" data-testid="fp-acknowledgement">
        <p className="text-[13.5px] text-[#1e3a8a] leading-relaxed">
          You are joining MySchoolRx as a Founding Partner. No payment is required. In return, we ask that you actively use the platform and share feedback that helps us improve it.
        </p>
        <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#2563eb]"
            data-testid="fp-agree-checkbox"
          />
          <span className="text-[13.5px] font-medium text-[#111827]">
            I understand and agree to participate as a Founding Partner and provide feedback.
          </span>
        </label>
      </div>

      {ctx && (
        <div className="mt-6 max-w-2xl rounded-lg border border-[#dbeafe] bg-[#eff6ff] px-4 py-3 text-[13px] text-[#1e40af]" data-testid="pilot-context-hint">
          {ctx === "school"
            ? "You came from the For Schools page. \u201CSingle School\u201D is highlighted below \u2014 you can still choose whichever option fits your organization."
            : ctx === "portfolio"
            ? "You came from the For Portfolios page. Multi-school options are highlighted below \u2014 you can still choose whichever option fits your organization."
            : null}
        </div>
      )}

      <div className={`mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity ${agreed ? "" : "opacity-50"}`} data-testid="pilot-orgtype-grid">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => choose(o)}
            disabled={!agreed}
            data-testid={`pilot-orgtype-${o.key}`}
            className={`text-left rounded-2xl border bg-white p-5 shadow-sm transition-all hover:border-[#2563eb] hover:shadow-md disabled:cursor-not-allowed ${isMatch(o) ? "border-[#2563eb] ring-2 ring-[#2563eb]/40" : "border-[#e5e7eb]"}`}
          >
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#eff6ff] text-[#2563eb]">
              <o.icon className="h-5 w-5" />
            </div>
            <div className="mt-3 text-[15px] font-bold text-[#111827] leading-snug">{o.label}</div>
            <p className="mt-1 text-[12.5px] text-[#6b7280] leading-relaxed">{o.desc}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#2563eb]">
              Continue <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </button>
        ))}
      </div>
      <a href="/diagnostic/login" className="mt-8 inline-block text-[13px] font-semibold text-[#374151] hover:text-[#111827]" data-testid="pilot-entry-signin">
        Already have an account? Sign in
      </a>
    </Shell>
  );
};

/* ── Welcome success screen ── */
const Welcome = ({ title, text, primaryLabel, onPrimary, secondaryLabel, onSecondary }) => (
  <Shell>
    <div className="max-w-xl mx-auto rounded-2xl border border-[#e5e7eb] bg-white p-8 text-center shadow-sm" data-testid="pilot-welcome">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#ecfdf5] text-[#059669]">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h1 className="mt-4 text-2xl font-bold text-[#0b1220]">{title}</h1>
      <p className="mt-3 text-[14px] text-[#4b5563] leading-relaxed">{text}</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={onPrimary} className="bg-[#007a9e] hover:bg-[#00607d] text-white font-semibold" data-testid="pilot-welcome-primary">
          {primaryLabel} <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
        {secondaryLabel && (
          <Button onClick={onSecondary} variant="outline" data-testid="pilot-welcome-secondary">{secondaryLabel}</Button>
        )}
      </div>
    </div>
  </Shell>
);

/* ── Single-school pilot onboarding ── */
export const PilotSingleSchool = () => {
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", preferred_name: "", email: "", password: "",
    phone: "", job_title: "", school_name: "", city: "", state: "", zip: "", school_type: "", master_school_id: null });
  const [consent, setConsent] = usePilotConsent();
  const [needConsent, setNeedConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onSchoolInput = (v) => {
    set("school_name", v); set("master_school_id", null);
    clearTimeout(debounce.current);
    if (v.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try { setResults(await pilotApi.schoolSearch(v, f.state)); } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const pick = (r) => {
    setF((p) => ({ ...p, school_name: r.name, master_school_id: r.id,
      city: r.city || p.city, state: r.state || p.state, school_type: r.school_type || p.school_type }));
    setResults([]);
  };

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.school_name) { toast.error("Please complete the required fields."); return; }
    if (!f.password || f.password.length < 8) { toast.error("Please choose a password of at least 8 characters."); return; }
    if (!consent) { setNeedConsent(true); toast.error("Please tick the Founding Partner consent box above the button."); return; }
    setSubmitting(true);
    try {
      const r = await pilotApi.submitSingleSchool({ ...f, consent: true, consent_at: consent.at });
      // Establish the school session and clear any ambient platform cookie.
      try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
      localStorage.setItem("diag_session", JSON.stringify({ user: r.user, tenant: r.tenant, session_token: r.session_token, remember: false, loginAt: new Date().toISOString() }));
      markBrowserSession();
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <Welcome
        title="Welcome to MySchoolRx Founding Partner Program"
        text="Your workspace is ready. You may begin your Diagnostic now. As a Founding Partner, no payment or purchase order is required. We appreciate your feedback as you work through the platform."
        primaryLabel="Start the Diagnostic" onPrimary={() => navigate("/diagnostic", { replace: true })}
        secondaryLabel="Invite School Users" onSecondary={() => navigate("/diagnostic", { replace: true })}
      />
    );
  }

  return (
    <Shell>
      <button onClick={() => navigate(`${ROUTE}/pilot`)} className="inline-flex items-center gap-1 text-[13px] text-[#6b7280] hover:text-[#111827]" data-testid="pilot-school-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mt-4 max-w-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0b1220]">Single-school Founding Partner setup</h1>
        <p className="mt-2 text-[14px] text-[#4b5563]">A few details and you'll be in your workspace.</p>
      </div>
      {consent && <div className="mt-5 max-w-2xl"><NoPaymentNote /></div>}

      <div className="mt-6 max-w-2xl rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name" required><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="pilot-school-first" /></Field>
          <Field label="Last name" required><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="pilot-school-last" /></Field>
          <Field label="Work email" required><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="pilot-school-email" /></Field>
          <Field label="Create a password" required><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" data-testid="pilot-school-password" /></Field>
          <Field label="Phone"><PhoneField value={f.phone} onChange={(v) => set("phone", v)} testid="pilot-school-phone" /></Field>
          <Field label="Job title"><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="pilot-school-title" /></Field>
        </div>

        <div className="relative">
          <Field label="School name" required>
            <Input value={f.school_name} onChange={(e) => onSchoolInput(e.target.value)} placeholder="Start typing to search the directory…" data-testid="pilot-school-name" autoComplete="off" />
          </Field>
          {(searching || results.length > 0) && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-[#e5e7eb] bg-white shadow-lg max-h-64 overflow-auto" data-testid="pilot-school-results">
              {searching && <div className="px-3 py-2 text-[13px] text-[#6b7280] flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>}
              {!searching && results.map((r) => (
                <button key={r.id} onClick={() => pick(r)} className="w-full text-left px-3 py-2 hover:bg-[#f3f4f6] text-[13px]" data-testid={`pilot-school-result-${r.id}`}>
                  <span className="font-medium text-[#111827]">{r.name}</span>
                  <span className="text-[#6b7280]"> — {r.city}, {r.state}</span>
                </button>
              ))}
            </div>
          )}
          {f.master_school_id && <p className="mt-1 text-[12px] text-[#059669]">Matched to an existing school in the directory.</p>}
          {!f.master_school_id && f.school_name.length > 1 && <p className="mt-1 text-[12px] text-[#6b7280]">Can't find it? We'll add it and flag it for review.</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="pilot-school-city" /></Field>
          <Field label="State"><Input value={f.state} onChange={(e) => set("state", e.target.value.toUpperCase())} maxLength={2} placeholder="" data-testid="pilot-school-state" /></Field>
          <Field label="ZIP"><Input value={f.zip} onChange={(e) => set("zip", e.target.value)} data-testid="pilot-school-zip" /></Field>
        </div>

        {(!consent || needConsent) && <InlineConsent checked={!!consent} onChange={setConsent} testid="pilot-school" />}
        <Button onClick={submit} disabled={submitting} className="w-full h-11 bg-[#007a9e] hover:bg-[#00607d] text-white font-semibold" data-testid="pilot-school-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Create my Founding Partner workspace
        </Button>
      </div>
    </Shell>
  );
};

/* ── Portfolio pilot onboarding ── */
export const PilotPortfolio = () => {
  const navigate = useNavigate();
  const [f, setF] = useState(() => {
    let orgType = "";
    try { orgType = new URLSearchParams(window.location.search).get("orgtype") || ""; } catch { /* ignore */ }
    return { first_name: "", last_name: "", preferred_name: "", email: "", password: "",
      phone: "", job_title: "", organization_name: "", organization_type: orgType, state: "", schools_overseen: "" };
  });
  const [consent, setConsent] = usePilotConsent();
  const [needConsent, setNeedConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || !f.organization_name) { toast.error("Please complete the required fields."); return; }
    if (!f.password || f.password.length < 8) { toast.error("Please choose a password of at least 8 characters."); return; }
    if (!consent) { setNeedConsent(true); toast.error("Please tick the Founding Partner consent box above the button."); return; }
    setSubmitting(true);
    try {
      await pilotApi.submitPortfolio({ ...f, consent: true, consent_at: consent.at, schools_overseen: parseInt(f.schools_overseen || "0", 10) });
      // Log in (platform) and clear any stale school session, then to the wizard.
      await axios.post(`${API}/api/auth/login`, { email: f.email, password: f.password }, { withCredentials: true });
      try { localStorage.removeItem("diag_session"); } catch {}
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <Welcome
        title="Welcome to MySchoolRx Founding Partner Program"
        text="Your Portfolio workspace is ready. Begin by selecting the schools you want to include and inviting a contact from each school. No payment or purchase order is required during the Founding Partner program."
        primaryLabel="Build My Portfolio" onPrimary={() => navigate("/portfolio/onboarding", { replace: true })}
      />
    );
  }

  return (
    <Shell>
      <button onClick={() => navigate(`${ROUTE}/pilot`)} className="inline-flex items-center gap-1 text-[13px] text-[#6b7280] hover:text-[#111827]" data-testid="pilot-portfolio-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mt-4 max-w-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0b1220]">Portfolio Founding Partner setup</h1>
        <p className="mt-2 text-[14px] text-[#4b5563]">Create your Portfolio, then add schools and invite contacts.</p>
      </div>
      {consent && <div className="mt-5 max-w-2xl"><NoPaymentNote /></div>}

      <div className="mt-6 max-w-2xl rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First name" required><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="pilot-portfolio-first" /></Field>
          <Field label="Last name" required><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="pilot-portfolio-last" /></Field>
          <Field label="Work email" required><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="pilot-portfolio-email" /></Field>
          <Field label="Create a password" required><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" data-testid="pilot-portfolio-password" /></Field>
          <Field label="Phone"><PhoneField value={f.phone} onChange={(v) => set("phone", v)} testid="pilot-portfolio-phone" /></Field>
          <Field label="Job title"><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="pilot-portfolio-jobtitle" /></Field>
          <Field label="Organization name" required><Input value={f.organization_name} onChange={(e) => set("organization_name", e.target.value)} data-testid="pilot-portfolio-org" /></Field>
          <Field label="What type of organization are you?" required>
            <select value={f.organization_type} onChange={(e) => set("organization_type", e.target.value)} data-testid="pilot-portfolio-orgtype"
              className="w-full h-10 rounded-md border border-[#d1d5db] bg-white px-3 text-sm">
              <option value="">Select organization type…</option>
              <option value="school_district">School District</option>
              <option value="charter_authorizer">Charter Authorizer</option>
              <option value="cmo">Charter Management Organization (CMO)</option>
              <option value="emo">Education Management Organization (EMO)</option>
              <option value="private_school_network">Private School Network</option>
              <option value="diocese">Diocese / Faith-Based School Network</option>
              <option value="state_education_agency">State Education Agency</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="State"><Input value={f.state} onChange={(e) => set("state", e.target.value.toUpperCase())} maxLength={2} placeholder="" data-testid="pilot-portfolio-state" /></Field>
          <Field label="Number of schools overseen"><Input type="number" value={f.schools_overseen} onChange={(e) => set("schools_overseen", e.target.value)} data-testid="pilot-portfolio-schools" /></Field>
        </div>

        {(!consent || needConsent) && <InlineConsent checked={!!consent} onChange={setConsent} testid="pilot-portfolio" />}
        <Button onClick={submit} disabled={submitting} className="w-full h-11 bg-[#007a9e] hover:bg-[#00607d] text-white font-semibold" data-testid="pilot-portfolio-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Create my Founding Partner Portfolio
        </Button>
      </div>
    </Shell>
  );
};

export default PilotEntry;
